// Favourite lists (DEV-51010): named groups of favourites, e.g. "Tue EV job".
// Pure helpers only. Server state lives in auth/hooks/useFavouriteLists.
//
// The name rules mirror backend/favourites/list_services.py so the sheet and
// dialogs can show the error inline without a round trip. The server checks
// again and its message wins if the two ever disagree (e.g. a list created
// on another device).

import type { FavouriteList } from '../types'

export const LIST_NAME_MAX_LENGTH = 40

// Toasts with an action ("Add to list", "Undo") stay up long enough to reach
// the button on a phone.
export const ACTION_TOAST_MS = 6000

/** Returns the message to show under the name field, or null if the name is fine. */
export function validateListName(
  name: string,
  lists: FavouriteList[],
  ignoreListId?: string,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name'
  // No length check: ListNameField stops input at LIST_NAME_MAX_LENGTH.
  const clash = lists.find(
    (list) => list.id !== ignoreListId && list.name.toLowerCase() === trimmed.toLowerCase(),
  )
  if (clash) return `You already have a list called ${clash.name}`
  return null
}

/** Most recently changed first. */
export function sortListsByRecent(lists: FavouriteList[]): FavouriteList[] {
  return [...lists].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
}

export function itemCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`
}

/** Toast copy after un-hearting: "Removed from favourites and 2 lists". */
export function removedFromFavouritesMessage(listCount: number): string {
  if (listCount === 0) return 'Removed from favourites'
  return `Removed from favourites and ${listCount} ${listCount === 1 ? 'list' : 'lists'}`
}

/**
 * The user-facing message from a favourite-list API error ({ error, code }),
 * or null for anything else (network failure, 5xx), where the caller shows
 * its own fallback.
 */
export function listErrorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null
  const data = (error as { data?: unknown }).data
  if (typeof data !== 'object' || data === null) return null
  const message = (data as { error?: unknown }).error
  return typeof message === 'string' ? message : null
}

const SAVE_LIST_FALLBACK_ERROR = "Couldn't save the list. Try again."

export type ListNameResult<T> = { ok: true; value: T } | { ok: false; error: string }

/**
 * Checks a list name locally, then saves it. Resolves to the saved value, or
 * to the error to show under the name field (the server's wording when it
 * rejects the name). Shared by the sheet's "New list" and the create and
 * rename dialogs.
 */
export async function saveListName<T>(
  name: string,
  lists: FavouriteList[],
  save: (name: string) => Promise<T>,
  ignoreListId?: string,
): Promise<ListNameResult<T>> {
  const localError = validateListName(name, lists, ignoreListId)
  if (localError) return { ok: false, error: localError }
  try {
    return { ok: true, value: await save(name) }
  } catch (error) {
    return { ok: false, error: listErrorMessage(error) ?? SAVE_LIST_FALLBACK_ERROR }
  }
}

// ---------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------

export function listsTabPath(tenantSlug: string | undefined): string {
  return `/${tenantSlug}/engineer/favourites?tab=lists`
}

export function favouriteListPath(tenantSlug: string | undefined, listId: string): string {
  return `/${tenantSlug}/engineer/favourites/lists/${listId}`
}

// Router state set by the Lists tab's cards. A list screen opened from there
// goes back through history, so Back never lands on a list the engineer
// just deleted; opened from anywhere else (a shared link) it replaces
// itself with the Lists tab instead.
export const FROM_LISTS_STATE = { fromLists: true } as const

export function cameFromLists(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { fromLists?: unknown }).fromLists === true
  )
}
